import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { AppColors } from "../../constants/AppColors";
import { StatisticsOverviewResponse } from "../../models/ResponseModels/StatisticsSummaryResponse";
import { formatMoney, formatPercent } from "./statisticsHelpers";

interface AnalyticsMetricGridProps {
  overview: StatisticsOverviewResponse;
  t: (key: string, values?: Record<string, string | number>) => string;
}

const AnalyticsMetricGrid = ({ overview, t }: AnalyticsMetricGridProps) => {
  const metrics = [
    {
      label: t("statistics.revenue"),
      value: formatMoney(overview.revenue, overview.currency),
      icon: <TrendingUpOutlinedIcon />,
    },
    {
      label: t("expenses.totalExpenses"),
      value: formatMoney(overview.total_expenses, overview.currency),
      icon: <TrendingDownOutlinedIcon />,
    },
    {
      label: t("statistics.expenseRatio"),
      value: formatPercent(overview.expense_ratio_percentage),
      icon: <DonutLargeOutlinedIcon />,
    },
    {
      label: t("expenses.manualExpenses"),
      value: formatMoney(overview.manual_expenses, overview.currency),
      icon: <TrendingDownOutlinedIcon />,
    },
    {
      label: t("expenses.recurringExpenses"),
      value: formatMoney(overview.recurring_expenses, overview.currency),
      icon: <RepeatOutlinedIcon />,
    },
    {
      label: t("expenses.expenses"),
      value: overview.expense_count,
      icon: <TrendingDownOutlinedIcon />,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
        gap: 2,
        mb: 2,
      }}
    >
      {metrics.map((item) => (
        <Card key={item.label} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 13, fontWeight: 700 }}
                >
                  {item.label}
                </Typography>

                <Typography sx={{ mt: 0.5, fontSize: 23, fontWeight: 950 }}>
                  {item.value}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "16px",
                  bgcolor: AppColors.primarySoft,
                  color: AppColors.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default AnalyticsMetricGrid;
